import { useCallback, useEffect, useMemo, useState } from "react";
import { Play, Plus, RotateCcw } from "lucide-react";
import { AgentOSLayout } from "@/components/agent-os/AgentOSLayout";
import { apiFetch, formatDateTime, inputClass, JsonPreview, Pill, Spinner } from "@/components/agent-os/shared";

const DEFAULT_INPUTS = {
    "operations.summary": "{}",
    "memory.write": JSON.stringify({ scope: "task", key: "mission.note", value: "Human-approved operational note", sensitivity: "internal" }, null, 2),
    "controlled.failure": "{}",
    "controlled.tool": JSON.stringify({ tool_id: "email.send", tool_input: { to: "example@example.com" } }, null, 2),
};

const DarkLabel = ({ children }) => <span className="mb-1.5 block text-[11px] font-medium uppercase tracking-[0.16em] text-ivory/55">{children}</span>;

export default function AgentOSTasks() {
    const [tasks, setTasks] = useState(null);
    const [agents, setAgents] = useState([]);
    const [selected, setSelected] = useState(null);
    const [form, setForm] = useState({ agent_id: "operations-intelligence", title: "", kind: "operations.summary", input: "{}", risk_level: "low", max_attempts: 1 });
    const [error, setError] = useState("");

    const load = useCallback(() => {
        apiFetch("/tasks").then((r) => (r.ok ? r.json() : [])).then(setTasks);
    }, []);
    useEffect(() => {
        load();
        apiFetch("/agents").then((r) => (r.ok ? r.json() : [])).then(setAgents);
    }, [load]);

    const agent = useMemo(() => agents.find((a) => a.agent_id === form.agent_id), [agents, form.agent_id]);
    useEffect(() => {
        if (agent && !agent.capabilities.includes(form.kind)) {
            const kind = agent.capabilities[0] || "operations.summary";
            setForm((f) => ({ ...f, kind, input: DEFAULT_INPUTS[kind] || "{}" }));
        }
    }, [agent, form.kind]);

    const create = async () => {
        setError("");
        let parsed;
        try { parsed = JSON.parse(form.input || "{}"); } catch { setError("Task input must be valid JSON."); return; }
        const res = await apiFetch("/tasks", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ ...form, input: parsed, max_attempts: Number(form.max_attempts) || 1 }) });
        const body = await res.json().catch(() => ({}));
        if (!res.ok) { setError(typeof body.detail === "string" ? body.detail : "Task could not be created."); return; }
        setForm((f) => ({ ...f, title: "" }));
        load();
    };

    const run = async (task) => {
        const res = await apiFetch(`/tasks/${task.task_id}/run`, { method: "POST" });
        if (res.ok) {
            const updated = await res.json();
            setSelected(updated);
            load();
        }
    };

    return (
        <AgentOSLayout title="Tasks" subtitle="Create bounded tasks, execute safe internal workflows, review results and inspect retry behavior.">
            <div className="grid gap-8 xl:grid-cols-[0.38fr_0.62fr]" data-testid="agent-tasks-page">
                <section className="h-fit border border-ink/10 bg-night p-6 text-ivory">
                    <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-champagne">Create task</p>
                    <div className="mt-6 space-y-4">
                        <div><DarkLabel>Agent</DarkLabel><select data-testid="task-agent" value={form.agent_id} onChange={(e) => setForm({ ...form, agent_id: e.target.value })} className={`${inputClass} cursor-pointer`}>{agents.map((a) => <option key={a.agent_id} value={a.agent_id}>{a.name}</option>)}</select></div>
                        <div><DarkLabel>Task kind</DarkLabel><select data-testid="task-kind" value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value, input: DEFAULT_INPUTS[e.target.value] || "{}" })} className={`${inputClass} cursor-pointer`}>{(agent?.capabilities || []).map((cap) => <option key={cap} value={cap}>{cap}</option>)}</select></div>
                        <div><DarkLabel>Title</DarkLabel><input data-testid="task-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={inputClass} placeholder="e.g. Safe operations snapshot" /></div>
                        <div className="grid grid-cols-2 gap-3">
                            <div><DarkLabel>Risk</DarkLabel><select data-testid="task-risk" value={form.risk_level} onChange={(e) => setForm({ ...form, risk_level: e.target.value })} className={`${inputClass} cursor-pointer`}>{["low", "medium", "high", "critical"].map((r) => <option key={r} value={r}>{r}</option>)}</select></div>
                            <div><DarkLabel>Max attempts</DarkLabel><input data-testid="task-attempts" type="number" min="1" max="3" value={form.max_attempts} onChange={(e) => setForm({ ...form, max_attempts: e.target.value })} className={inputClass} /></div>
                        </div>
                        <div><DarkLabel>Input JSON</DarkLabel><textarea data-testid="task-input" rows={8} value={form.input} onChange={(e) => setForm({ ...form, input: e.target.value })} className={`${inputClass} resize-y font-mono text-xs`} /></div>
                        {error && <p data-testid="task-error" className="border-l-2 border-champagne pl-4 text-sm text-ivory/75">{error}</p>}
                        <button type="button" data-testid="task-create" onClick={create} className="inline-flex w-full items-center justify-center gap-2 bg-ivory px-5 py-3.5 text-xs font-semibold uppercase tracking-[0.14em] text-ink transition-colors hover:bg-champagne"><Plus size={14} /> Create Task</button>
                    </div>
                </section>

                <section>
                    {!tasks ? <Spinner /> : (
                        <div className="border-t border-ink/10" data-testid="agent-task-list">
                            {tasks.map((task) => (
                                <article key={task.task_id} data-testid={`task-row-${task.task_id}`} className="grid gap-4 border-b border-ink/10 py-5 lg:grid-cols-[1fr_auto] lg:items-center">
                                    <button type="button" onClick={() => setSelected(task)} className="min-w-0 text-left">
                                        <div className="flex flex-wrap items-center gap-2"><Pill value={task.status} /><Pill value={task.risk_level} risk /></div>
                                        <h2 className="mt-3 truncate font-serif text-2xl font-medium">{task.title}</h2>
                                        <p className="mt-1 text-xs text-ink/50">{task.agent_id} · {task.kind} · attempts {task.attempts}/{task.max_attempts} · {formatDateTime(task.created_at)}</p>
                                    </button>
                                    {!["completed", "dead", "cancelled"].includes(task.status) && (
                                        <button type="button" data-testid={`task-run-${task.task_id}`} onClick={() => run(task)} className="inline-flex items-center gap-2 bg-forest px-4 py-2.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-ivory hover:bg-forest-deep">
                                            {task.attempts ? <RotateCcw size={14} /> : <Play size={14} />} {task.attempts ? "Retry" : "Run"}
                                        </button>
                                    )}
                                </article>
                            ))}
                            {tasks.length === 0 && <p className="py-14 text-center text-sm text-ink/50">No tasks yet.</p>}
                        </div>
                    )}
                </section>
            </div>

            {selected && (
                <div className="fixed inset-0 z-50" data-testid="task-detail">
                    <div className="absolute inset-0 bg-night/55" onClick={() => setSelected(null)} />
                    <aside className="absolute right-0 top-0 h-full w-full max-w-2xl overflow-y-auto bg-ivory p-7 sm:p-10">
                        <div className="flex flex-wrap items-center gap-2"><Pill value={selected.status} /><Pill value={selected.risk_level} risk /></div>
                        <h2 className="mt-4 font-serif text-3xl font-medium">{selected.title}</h2>
                        <p className="mt-2 text-sm text-ink/55">{selected.agent_id} · {selected.kind} · {selected.task_id}</p>
                        {selected.error && <p className="mt-5 border-l-2 border-red-900 pl-4 text-sm text-red-900">{selected.error}</p>}
                        <div className="mt-7"><p className="mb-3 text-[11px] font-semibold uppercase tracking-[0.2em] text-ink/45">Result</p><JsonPreview value={selected.result || { status: selected.status }} /></div>
                        <div className="mt-7"><p className="mb-3 text-[11px] font-semibold uppercase tracking-[0.2em] text-ink/45">Input</p><JsonPreview value={selected.input || {}} /></div>
                    </aside>
                </div>
            )}
        </AgentOSLayout>
    );
}
